import { useCallback, useEffect, useState } from 'react';
import { getStoredUser, parseJsonSafe } from '../api/client';
import {
  acceptTutorLinkRequest, approveGuardianRelationshipRequest,
  approveTutorLinkRequestAsGuardian, createTutorLinkRequest,
  getMyGuardianRelationshipRequests, getMyTutorLinkRequests, rejectTutorLinkRequest,
} from '../api/me';

function TutorLinkRequestsPage() {
  const role = getStoredUser()?.role;
  const [studentEmail, setStudentEmail] = useState('');
  const [requests, setRequests] = useState([]);
  const [relationshipRequests, setRelationshipRequests] = useState([]);
  const [message, setMessage] = useState('');
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    const [response, relationshipResponse] = await Promise.all([
      getMyTutorLinkRequests(),
      role === 'PARENT' ? getMyGuardianRelationshipRequests() : null,
    ]);
    const data = await parseJsonSafe(response);
    if (!response.ok) throw new Error(data?.detail || 'Could not load tutor link requests.');
    let relationshipData = [];
    if (relationshipResponse) {
      relationshipData = await parseJsonSafe(relationshipResponse);
      if (!relationshipResponse.ok) {
        if (relationshipResponse.status === 403) {
          setMessage(relationshipData?.detail || 'Verify your email before confirming a guardian relationship.');
          relationshipData = [];
        } else {
          throw new Error(relationshipData?.detail || 'Could not load guardian relationship requests.');
        }
      }
    }
    setRequests(Array.isArray(data) ? data : []);
    setRelationshipRequests(Array.isArray(relationshipData) ? relationshipData : []);
  }, [role]);

  useEffect(() => { load().catch((error) => setMessage(error.message)); }, [load]);

  const submitRequest = async (event) => {
    event.preventDefault();
    setBusy(true); setMessage('');
    try {
      const response = await createTutorLinkRequest(studentEmail.trim());
      const data = await parseJsonSafe(response);
      if (!response.ok) throw new Error(data?.detail || 'Could not request the tutor link.');
      setStudentEmail('');
      setMessage('Request sent. The student must accept before any link is created.');
      await load();
    } catch (error) { setMessage(error.message); } finally { setBusy(false); }
  };

  const act = async (request) => {
    setBusy(true); setMessage('');
    try {
      const response = role === 'STUDENT'
        ? await acceptTutorLinkRequest(request.id)
        : await approveTutorLinkRequestAsGuardian(request.id);
      const data = await parseJsonSafe(response);
      if (!response.ok) throw new Error(data?.detail || 'Could not approve the tutor link.');
      setMessage(data.status === 'PENDING_GUARDIAN'
        ? 'Accepted. A verified linked parent must now approve.'
        : 'Tutor link approved.');
      await load();
    } catch (error) { setMessage(error.message); } finally { setBusy(false); }
  };

  const decline = async (request) => {
    setBusy(true); setMessage('');
    try {
      const response = await rejectTutorLinkRequest(request.id);
      const data = await parseJsonSafe(response);
      if (!response.ok) throw new Error(data?.detail || 'Could not decline the tutor link.');
      setMessage('Tutor link declined.');
      await load();
    } catch (error) { setMessage(error.message); } finally { setBusy(false); }
  };

  const confirmRelationship = async (request) => {
    setBusy(true); setMessage('');
    try {
      const response = await approveGuardianRelationshipRequest(request.cohort_request_id);
      const data = await parseJsonSafe(response);
      if (!response.ok) throw new Error(data?.detail || 'Could not confirm the guardian relationship.');
      setMessage('Relationship confirmed. You can now review the separate tutor link request.');
      await load();
    } catch (error) { setMessage(error.message); } finally { setBusy(false); }
  };

  return (
    <div className="page page-shell page-stack">
      <section className="page-header"><div><p className="eyebrow">Safeguarded linking</p><h1>Tutor link requests</h1><p>Record a tutor relationship only after the student, and a parent for under-18s, approves it.</p></div></section>
      {role === 'TUTOR' ? (
        <form className="card page-stack" onSubmit={submitRequest}>
          <h2>Request a student link</h2>
          <p>Enter the email address the student already uses for Sumrise.</p>
          <label htmlFor="link-student-email">Student email</label>
          <input id="link-student-email" type="email" required value={studentEmail} onChange={(event) => setStudentEmail(event.target.value)} />
          <button className="btn btn-primary" type="submit" disabled={busy}>Send request</button>
        </form>
      ) : null}
      {role === 'PARENT' && relationshipRequests.length > 0 ? (
        <section className="card page-stack">
          <h2>Confirm your relationship</h2>
          <p>Confirming grants you access to the named student. It does not approve their tutor; you will review that separately next.</p>
          <ul className="data-list">{relationshipRequests.map((request) => (
            <li className="data-list-item" key={request.cohort_request_id}>
              <strong>{request.first_name} {request.last_name}</strong>
              <p>Owning tutor: {request.tutor_email}</p>
              <button className="btn btn-primary" type="button" disabled={busy} onClick={() => confirmRelationship(request)}>I am this student’s parent or guardian</button>
            </li>
          ))}</ul>
        </section>
      ) : null}
      <section className="card page-stack">
        <h2>{role === 'TUTOR' ? 'Your requests' : 'Tutor link requests and status'}</h2>
        {requests.length === 0 ? <p>No tutor link requests.</p> : (
          <ul className="data-list">{requests.map((request) => (
            <li className="data-list-item" key={request.id}>
              <strong>{request.student_display_name}</strong>
              <p>Tutor: {request.tutor_email}</p><p>Status: {request.status.replaceAll('_', ' ').toLowerCase()}</p>
              {role === 'STUDENT' && request.status === 'PENDING_STUDENT' ? <div><button className="btn btn-primary" type="button" disabled={busy} onClick={() => act(request)}>Accept tutor</button> <button className="btn btn-secondary" type="button" disabled={busy} onClick={() => decline(request)}>Decline</button></div> : null}
              {role === 'PARENT' && request.status === 'PENDING_GUARDIAN' ? <div><button className="btn btn-primary" type="button" disabled={busy} onClick={() => act(request)}>Approve tutor link</button> <button className="btn btn-secondary" type="button" disabled={busy} onClick={() => decline(request)}>Decline</button></div> : null}
            </li>
          ))}</ul>
        )}
        {message ? <p role="status" className="notice notice-info">{message}</p> : null}
      </section>
    </div>
  );
}

export default TutorLinkRequestsPage;
